import React from 'react';
import { connect } from 'react-redux';
// @rbac
import { useB2BPermissions } from '../../../../context/B2BPermissionsContext';
// @design-system
import TitleBanner from '../../../../design-system/components/Banners/TitleBanner';
import Select from '../../../../design-system/components/Form/Select/Select';
import Table from '../../../../design-system/components/Table/Table';
import LoaderContent from '../../../../design-system/components/Loader/LoaderContent';
import Toast from '../../../../design-system/components/Toast/Toast';
import NothingFound from '../../../../components/NothingFound/NothingFound';
// @material-core
import { Grid, Card, CardContent, Box, Button, Chip, Typography } from '@material-ui/core';
// @thirdparty
import { isNil } from 'ramda';
// @local
import { usageDetailedColumns, usageTotalOnlyColumns } from '../ContractsColumns';
import { getContractUsage } from '../contractsMockService';

const ContractsUsagePage = () => {
  // Mismo contrato de scope que el módulo B2B: empresas filtradas internal/external.
  const { assignedCompanies, autoSelectCompany, assignedCompanyName } = useB2BPermissions();

  const [loading, setLoading] = React.useState(false);
  const [toast, setToast] = React.useState({ toastProps: { message: '', open: false } });
  const [companyItems, setCompanyItems] = React.useState([]);
  const [selectedCompany, setSelectedCompany] = React.useState('');
  const [usage, setUsage] = React.useState(null);
  const [hasSearched, setHasSearched] = React.useState(false);

  React.useEffect(() => {
    const items = (assignedCompanies || []).map((c) => ({
      id: c.company_name,
      label: c.company_name,
      value: c.company_name,
    }));
    setCompanyItems(items);

    // Autoselección cuando el usuario tiene una sola empresa (igual que Listado).
    if (autoSelectCompany) {
      setSelectedCompany(autoSelectCompany.company_name);
      fetchUsage(autoSelectCompany.company_name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedCompanyName.join('|')]);

  const fetchUsage = async (companyTag) => {
    if (isNil(companyTag) || companyTag === '') {
      setUsage(null);
      return;
    }
    try {
      setLoading(true);
      const { success, data } = await getContractUsage({ companyTag });
      setUsage(success ? data : null);
      setHasSearched(true);
    } catch (err) {
      setToast({ toastProps: { severity: 'error', open: true, message: err?.message || 'Ops! Ha ocurrido un error' } });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (isNil(selectedCompany) || selectedCompany === '') {
      setToast({ toastProps: { severity: 'warning', open: true, message: 'Seleccione una empresa para consultar el consumo.' } });
      return;
    }
    fetchUsage(selectedCompany);
  };

  const isDetailed = usage && usage.mode === 'detailed';
  const columns = isDetailed ? usageDetailedColumns : usageTotalOnlyColumns;

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TitleBanner
            title="Consumo de licencias"
            desc="Consulta para la empresa y para usuarios internos con acceso a esa empresa."
          />
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Grid container spacing={2} alignItems="flex-end">
                <Grid item xs={12} md={4}>
                  <Select
                    items={companyItems}
                    label="Empresa"
                    name="company"
                    value={selectedCompany}
                    onChange={(e) => setSelectedCompany(e.target.value)}
                    disabled={!companyItems.length || loading}
                    size="medium"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSearch}
                    disabled={loading || isNil(selectedCompany) || selectedCompany === ''}
                    style={{ height: 44 }}
                  >
                    Buscar
                  </Button>
                </Grid>
              </Grid>

              <LoaderContent loading={loading} minHeight={400}>
                {isNil(selectedCompany) || selectedCompany === '' ? (
                  !loading && (
                    <NothingFound
                      width="200px"
                      caption="Seleccione una empresa para consultar el consumo."
                      subcaption="El consumo se filtra por la empresa que tenga configurada (internal/external)."
                    />
                  )
                ) : usage ? (
                  <Box mt={2}>
                    <Chip
                      size="small"
                      label={
                        isDetailed
                          ? 'Contrato vigente · con desglose'
                          : 'Contrato vigente · solo total'
                      }
                      style={{ backgroundColor: '#ecfdf3', color: '#067647', fontWeight: 600 }}
                    />

                    {/* Métricas resumidas. Estilo explícito (fondo blanco + borde
                        gris claro + texto oscuro) para que NO hereden el fondo
                        oscuro del tema que aplica a Card variant="outlined". */}
                    <Grid container spacing={2} className="mt-2 mb-2">
                      {[
                        { label: 'Comprado', value: usage.purchased },
                        { label: 'Asignado', value: usage.assigned },
                        { label: 'Disponible', value: usage.available },
                        { label: 'Uso', value: `${usage.usagePct}%` },
                      ].map((stat) => (
                        <Grid item xs={6} md={3} key={stat.label}>
                          <Box
                            style={{
                              backgroundColor: '#ffffff',
                              border: '1px solid #e6e8ee',
                              borderRadius: 12,
                              padding: '10px 14px 16px',
                            }}
                          >
                            <Typography variant="caption" style={{ color: '#667085' }}>
                              {stat.label}
                            </Typography>
                            <Typography variant="h5" style={{ fontWeight: 700, color: '#1c1c1f' }}>
                              {stat.value}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>

                    {/* Barra de uso */}
                    <Box
                      style={{
                        height: 14,
                        background: '#e9edf5',
                        borderRadius: 999,
                        overflow: 'hidden',
                        margin: '16px 0',
                      }}
                      aria-label={`${usage.assigned} de ${usage.purchased} licencias asignadas`}
                    >
                      <Box style={{ height: '100%', width: `${usage.usagePct}%`, background: '#6236e8' }} />
                    </Box>

                    <Typography variant="caption" className="text-dark-black-200" display="block" gutterBottom>
                      {isDetailed
                        ? `${usage.assigned} de ${usage.purchased} licencias asignadas. Solo se pueden asignar los tipos comprados. Freemium no descuenta.`
                        : `No hay compra por tipo. El disponible del contrato es ${usage.available}. La tabla muestra cuántas hay de cada membresía, aunque no se hayan comprado por separado.`}
                    </Typography>

                    <Box mt={1}>
                      <Table columns={columns} rows={usage.rows} loading={false} />
                    </Box>
                  </Box>
                ) : (
                  !loading &&
                  hasSearched && (
                    <NothingFound
                      width="200px"
                      caption="No se encontró un contrato vigente para esta empresa."
                      subcaption="Verifica que la empresa tenga un contrato activo."
                    />
                  )
                )}
              </LoaderContent>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Toast
        {...toast.toastProps}
        duration={5000}
        onFinished={() => setToast({ toastProps: { ...toast.toastProps, open: false } })}
      />
    </>
  );
};

const mapStateToProps = (state) => ({ ...state.userReducer });

export default connect(mapStateToProps, {})(ContractsUsagePage);
